import { memo, useEffect } from "react";
import { toast } from "react-toastify";
import styles from "./LoanSummaryPage.module.scss";
import { useGetLoanSummaryQuery } from "../../../service/bps-admin-service";
import CommonLoadingSpinnerForPage from "../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { useConfigLayout } from "../../../hooks/useConfigLayout";

export type LoanSummaryResponse = {
	amountDisbursedTotal: number;
	amountRepaidTotal: number;
	amountDisbursedRunning: number;
	expectedInterestRunning: number;
	amountDisbursedPaid: number;
	expectedInterestPaid: number;
	amountDisbursedPastDue: number;
	expectedInterestPastDue: number;
	penaltyPastDue: number;
};

type Props = {};

const LoanSummaryPage = memo((props: Props) => {
	const { data: loanSummaryData, isLoading, error } = useGetLoanSummaryQuery();

	useConfigLayout({
					showCountrySelector: true,
					utility: null,
					showExportButton: false,
					showBreadCrumb: false,
					exportHandler: () => {},
				});

	const defaultData: LoanSummaryResponse = {
		amountDisbursedTotal: 0,
		amountRepaidTotal: 0,
		amountDisbursedRunning: 0,
		expectedInterestRunning: 0,
		amountDisbursedPaid: 0,
		expectedInterestPaid: 0,
		amountDisbursedPastDue: 0,
		expectedInterestPastDue: 0,
		penaltyPastDue: 0,
	};

	useEffect(() => {
		if (error) {
			if ("name" in error && "message" in error) {
				toast.error(`${error.name} - ${error.message}`);
			} else {
				toast.error("There was an error fetching loan summary data");
			}
		}
	}, [error]);

	const data = loanSummaryData || defaultData;

	return (
		<CommonLoadingSpinnerForPage loading={isLoading}>
			<div className={styles.loanSummary}>
				<div className={styles.loanSummary__header}>Loan Section</div>
				<div className={styles.loanSummary__content}>
					<div className={styles.card}>
						<div className={styles.card__header}>All Loans</div>
						<div className={styles.card__body}>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Amount Disbursed</div>
								<div className={styles.card__value}>
									{data.amountDisbursedTotal.toLocaleString()}
									<span className={styles.card__ngn}>NGN</span>
								</div>
							</div>
							<div className={styles.card__separator}></div>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Amount Repaid</div>
								<div className={styles.card__value}>
									{data.amountRepaidTotal.toLocaleString()}
									<span className={styles.card__ngn}>NGN</span>
								</div>
							</div>
							<div className={styles.card__separator}></div>
						</div>
					</div>
					<div className={styles.card}>
						<div className={styles.card__header}>Running Loans</div>
						<div className={styles.card__body}>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Amount Disbursed</div>
								<div className={styles.card__value}>
									{data.amountDisbursedRunning.toLocaleString()}
									<span className={styles.card__ngn}>NGN</span>
								</div>
							</div>
							<div className={styles.card__separator}></div>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Expected Interest</div>
								<div className={styles.card__value}>{(data.expectedInterestRunning * 100).toLocaleString()}%</div>
							</div>
							<div className={styles.card__separator}></div>
						</div>
					</div>
					<div className={styles.card}>
						<div className={styles.card__header}>Paid Loans</div>
						<div className={styles.card__body}>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Amount Disbursed</div>
								<div className={styles.card__value}>
									{data.amountDisbursedPaid.toLocaleString()}
									<span className={styles.card__ngn}>NGN</span>
								</div>
							</div>
							<div className={styles.card__separator}></div>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Expected Interest</div>
								<div className={styles.card__value}>{(data.expectedInterestPaid * 100).toLocaleString()}%</div>
							</div>
							<div className={styles.card__separator}></div>
						</div>
					</div>
					<div className={styles.card}>
						<div className={styles.card__header}>Past Due Loans</div>
						<div className={styles.card__body}>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Amount Disbursed</div>
								<div className={styles.card__value}>
									{data.amountDisbursedPastDue.toLocaleString()}
									<span className={styles.card__ngn}>NGN</span>
								</div>
							</div>
							<div className={styles.card__separator}></div>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Expected Interest</div>
								<div className={styles.card__value}>{(data.expectedInterestPastDue * 100).toLocaleString()}%</div>
							</div>
							<div className={styles.card__separator}></div>
							<div className={styles.card__property}>
								<div className={styles.card__label}>Penalty</div>
								<div className={styles.card__value}>{data.penaltyPastDue.toLocaleString()}%</div>
							</div>
							<div className={styles.card__separator}></div>
						</div>
					</div>
				</div>
			</div>
		</CommonLoadingSpinnerForPage>
	);
});

export default LoanSummaryPage;
